import { useState, useEffect, useCallback } from "react";
import api from "../../api";
import useBackdropClose from "../../hooks/useBackdropClose";
import { isActiveStaff, sortEndedLast, withEndedLabel } from "../../utils/contractEnded";

const toKST = () => new Date(new Date().getTime() + 9 * 60 * 60 * 1000);
const thisYear  = toKST().getFullYear();
const thisMonth = String(toKST().getMonth() + 1).padStart(2, "0");

function getWeekRange() {
  const now = toKST();
  const day = now.getDay();
  const mon = new Date(now); mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
  const sun = new Date(mon);  sun.setDate(mon.getDate() + 6);
  return [mon.toISOString().slice(0, 10), sun.toISOString().slice(0, 10)];
}

// 분 → "N시간 M분" 또는 "N시간" 표기
const fmtMin = (m) => {
  const mm = Math.max(0, Math.round(m || 0));
  if (mm === 0) return "0분";
  if (mm < 60) return `${mm}분`;
  const h = Math.floor(mm / 60);
  const r = mm % 60;
  return r ? `${h}시간 ${r}분` : `${h}시간`;
};

// 주말(토, 일)을 제외한 평일 일수 계산
function getWorkingDays(startStr, endStr) {
  const start = new Date(startStr);
  const end = new Date(endStr);
  let count = 0;
  const cur = new Date(start);
  while (cur <= end) {
    const day = cur.getDay();
    if (day !== 0 && day !== 6) {
      count++;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

const LEAVE_CATEGORIES = ["연차", "반차", "대체휴무", "특별휴가"];

// 세 소스 합산 통계 처리 함수
function combineTotal(d) {
  if (!d) return { count: 0, total_minutes: 0, avg_difficulty: 0 };
  
  let totalCount = 0;
  let totalMinutes = 0;
  let weightedDifficultySum = 0;
  let difficultyCount = 0; // ★ 난이도가 지정된 건만 따로 체크할 카운터

  ["calendar", "task", "daily"].forEach(key => {
    const src = d[key];
    if (!src || !Array.isArray(src.items)) return;

    src.items.forEach(it => {
      // 휴가성 항목 제외
      if (LEAVE_CATEGORIES.includes(it.category || it.category_name)) return;

      const mins = Number(it.minutes) || 0;
      const diff = Number(it.difficulty) || 0;

      totalCount += 1;
      totalMinutes += mins;

      // ★ 난이도가 0보다 큰(지정된) 건만 가중치 및 평균 계산에 포함
      if (diff > 0) {
        difficultyCount += 1;
        weightedDifficultySum += diff;
      }
    });
  });

  // 평균 계산 시 분모를 전체 건수가 아닌 '난이도가 지정된 건수'로 변경
  const avg = difficultyCount ? Math.round((weightedDifficultySum / difficultyCount) * 10) / 10 : 0;
  return { count: totalCount, total_minutes: totalMinutes, avg_difficulty: avg };
}

const SOURCE_META = {
  calendar: { label: "캘린더 업무일정", icon: "📅", color: "#3b82f6" },
  task:     { label: "지시 업무",       icon: "📋", color: "#8b5cf6" },
  daily:    { label: "일상 업무",       icon: "⚡", color: "#10b981" },
};

export default function WorkPerformanceDashboardPage() {
  const detailBackdrop = useBackdropClose(() => setDetail(null));
  const permBackdrop = useBackdropClose(() => setShowPerm(false));
  const { currentName, myRank, myId } = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("user"));
      return {
        currentName: u?.name || u?.username || "—",
        myRank: u?.position_rank ?? 99,
        myId: u?.id ?? null,
      };
    } catch { return { currentName: "—", myRank: 99, myId: null }; }
  })();

  const canViewOthers = myRank <= 3;

  const [startDate, setStartDate] = useState(`${thisYear}-${thisMonth}-01`);


  const [endDate,   setEndDate]   = useState(toKST().toISOString().slice(0, 10));
  const [empFilter, setEmpFilter] = useState("");  
  const [empList,   setEmpList]   = useState([]);

  
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [err,       setErr]       = useState("");
  const [detail,    setDetail]    = useState(null); // 드릴다운 모달: { title, color, items }

  // 🔒 열람 권한 (백엔드 연동)
  const [allowedIds, setAllowedIds] = useState(null);   // null = 로딩 중
  const [canManage,  setCanManage]  = useState(false);
  const [showPerm,   setShowPerm]   = useState(false);
  const [allUsers,   setAllUsers]   = useState([]);
  const [draftIds,   setDraftIds]   = useState([]);

  useEffect(() => {
    api.get("work-performance/viewers/")
      .then(r => { setAllowedIds(r.data.viewer_ids || []); setCanManage(!!r.data.can_manage); })
      .catch(() => setAllowedIds([]));
  }, []);

  const canView = canManage || (Array.isArray(allowedIds) && allowedIds.includes(myId));

  useEffect(() => {
    if (!canViewOthers) return;


    api.get("users/users/").then(r => {
      const raw = Array.isArray(r.data) ? r.data : (r.data.results || []);
      // 조회용 목록 — 계약종료자도 과거 성과를 볼 수 있게 남기고 맨 아래로(표기는 렌더링 시점)
      setEmpList(sortEndedLast(raw.filter(u => u.name && !u.name.includes("테스트"))));
    }).catch(() => {});
  }, [canViewOthers]);

  const load = useCallback(async () => {
    if (!canView) return;          // ★ 추가 -------------------------------------------- 접근 권한
    setLoading(true); setErr("");
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (empFilter) params.employee = empFilter; 
      const { data } = await api.get("work-performance/summary/", { params });
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "데이터를 불러오지 못했습니다.");
      setData(null);
    } finally { setLoading(false); }
  }, [startDate, endDate, empFilter, canView]);   // ★ canView 추가 --------------------- 점근 권한

  useEffect(() => { load(); }, [load]);

  const openDetail = (categoryName, items, color) =>
    setDetail({ title: categoryName || "미지정", color, items: Array.isArray(items) ? items : [] });

  // 모달 열기/저장
  const openPerm = () => {
    api.get("users/users/").then(r => {
      const raw = Array.isArray(r.data) ? r.data : (r.data.results || []);
      // 열람 권한 부여 대상 — 계약종료자 제외(서버 판정)
      setAllUsers(raw.filter(u => u.name && isActiveStaff(u) && !u.name.includes("테스트")));
      setDraftIds(allowedIds || []);
      setShowPerm(true);
    });
  };
  const toggleDraft = (id) =>
    setDraftIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const savePerm = async () => {
    try {
      await api.put("work-performance/viewers/", { viewer_ids: draftIds });
      setAllowedIds(draftIds);
      setShowPerm(false);
      alert("✅ 저장되었습니다.");
    } catch {
      alert("저장 중 오류가 발생했습니다.");
    }
  };

  // 🔒 권한 로딩 중
  if (allowedIds === null) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#94a3b8",
        fontFamily: "'Noto Sans KR', sans-serif", fontSize: 14 }}>
        확인 중…
      </div>
    );
  }
  // 🔒 권한 없음
  if (!canView) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#94a3b8",
        fontFamily: "'Noto Sans KR', sans-serif", fontSize: 14 }}>
        이 페이지는 접근 권한이 없습니다.
      </div>
    );
  }





  // 실제 총 업무건수, 실제 총 근무한 시간 계산용 객체
  const total = data ? combineTotal(data) : { count: 0, total_minutes: 0, avg_difficulty: 0 };

  // ── ⏱️ 근무 가능 시간 및 휴가 계산 ──
  const workingDays = getWorkingDays(startDate, endDate);
  const totalWorkingMinutes = workingDays * 8 * 60; // 2. 기간 총 근무 가능 시간

  const holidayMinutes = (data?.holiday_count || 0) * 8 * 60; // 3. 공휴일 차감 분
  
  const getLeaveMinutesFromItems = (catNames) => {
    if (!data) return 0;
    let sum = 0;
    ["calendar", "task", "daily"].forEach(k => {
      (data[k]?.items || []).forEach(it => {
        if (catNames.includes(it.category || it.category_name)) {
          sum += (Number(it.minutes) || 0);
        }
      });
    });
    return sum;
  };

  const substituteLeaveMinutes = data?.substitute_leave_minutes ?? getLeaveMinutesFromItems(["대체휴무"]); // 4. 대체휴무 분
  const annualLeaveMinutes = data?.annual_leave_minutes ?? getLeaveMinutesFromItems(["연차", "반차"]); // 5. 연차/반차 분

  // 6. 실제 근무 가능한 시간 = 총 근무가능 시간 - 공휴일 - 대체휴무 - 연차/반차
  const actualAvailableMinutes = totalWorkingMinutes - holidayMinutes - substituteLeaveMinutes - annualLeaveMinutes;

  // 핵심 업무 (난이도 7 이상, 휴가성 항목 제외)
  const keyTasks = (() => {
    if (!data) return [];
    const all = [];
    ["calendar", "task", "daily"].forEach(k => {
      const items = data[k]?.items || [];
      items.forEach(it => {
        if (LEAVE_CATEGORIES.includes(it.category || it.category_name)) return;
        const diff = Number(it.difficulty) || 0;
        const mins = Number(it.minutes) || 0;
        if (diff >= 7) {
          all.push({
            title: it.title || "(제목 없음)",
            difficulty: diff,
            minutes: mins,
            date: it.date || "",
            source: k,
          });
        }
      });
    });
    all.sort((a, b) => (b.difficulty - a.difficulty) || (b.minutes - a.minutes));
    return all;
  })();

  const scopeLabel = (() => {
    if (empFilter === "all") return "팀 전체";
    if (empFilter && empFilter !== String(myId)) {
      const e = empList.find(u => String(u.id) === String(empFilter));
      return e ? withEndedLabel(e.name, e.is_contract_ended) : "직원";
    }
    return currentName;
  })();

  // 카드 공통 스타일
  const card = {
    background: "#fff", border: "0.5px solid #e2e8f0",
    borderRadius: 12, padding: "16px 18px",
  };
  const ghostBtn = {
    padding: "5px 12px", borderRadius: 6, border: "0.5px solid #e2e8f0",
    background: "#fff", color: "#0f172a", fontSize: 12, cursor: "pointer",
    whiteSpace: "nowrap",
  };
  const dateInput = {
    border: "0.5px solid #e2e8f0", borderRadius: 6,
    padding: "5px 8px", fontSize: 13, outline: "none", color: "#0f172a",
  };

  return (
    <div style={{
      display: "flex", gap: 16, alignItems: "flex-start",
      padding: "16px 20px",
      fontFamily: "'Noto Sans KR', sans-serif",
      color: "#0f172a",
    }}>

    {/* ── 좌측 메인 영역 ── */}
    <div style={{ flex: "1 1 0", minWidth: 0 }}>

      {/* 타이틀 */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 26, fontWeight: 600 }}>업무 성과</span>
        <span style={{ fontSize: 14, color: "#64748b" }}>
          {scopeLabel} · {startDate} ~ {endDate}
        </span>
      </div>

      {/* 툴바 */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap",
      }}>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
          style={{ ...dateInput, width: 150, flex: "0 0 auto" }} />
        <span style={{ color: "#94a3b8" }}>~</span>
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
          style={{ ...dateInput, width: 150, flex: "0 0 auto" }} />

        <button style={{ ...ghostBtn, flex: "0 0 auto", width: "fit-content" }} onClick={() => {
          setStartDate(`${thisYear}-${thisMonth}-01`);
          setEndDate(toKST().toISOString().slice(0, 10));
        }}>이번 달</button>
        <button style={{ ...ghostBtn, flex: "0 0 auto", width: "fit-content" }} onClick={() => {
          const [s, e] = getWeekRange(); setStartDate(s); setEndDate(e);
        }}>이번 주</button>

        {canViewOthers && (
          <select
            value={empFilter}
            onChange={e => setEmpFilter(e.target.value)}
            style={{ ...dateInput, cursor: "pointer", width: 160, minWidth: 160, maxWidth: 160, flex: "0 0 auto" }}
          >
            <option value="">본인 ({currentName})</option>
            <option value="all">팀 전체</option>
            {empList.map(u => <option key={u.id} value={u.id}>{withEndedLabel(u.name, u.is_contract_ended)}</option>)}
          </select>
        )}


        <button style={{ ...ghostBtn, flex: "0 0 auto", width: "fit-content" }} onClick={load}>↺ 새로고침</button>

        {canManage && (
          <button style={{ ...ghostBtn, flex: "0 0 auto", width: "fit-content" }} onClick={openPerm}>
            🔑 보기 권한
          </button>
        )}
      </div>


      {/* ── 📊 종합 요약 대시보드 (9개 칸 순차 정렬 완료) ── */}
      {!loading && data && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "1.2fr repeat(8, 1fr)",
          alignItems: "center",
          gap: 12,
          background: "linear-gradient(135deg, #1e293b, #0f172a)",
          color: "#fff",
          borderRadius: 12,
          padding: "16px 14px",
          marginBottom: 16,
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
          overflowX: "auto"
        }}>
          {/* 1. 종합 요약 라벨 */}
          <div style={{ fontSize: 12, color: "#cbd5e1", borderRight: "1px solid #334155", paddingRight: 8 }}>
            <span style={{ fontWeight: 700, color: "#38bdf8", whiteSpace: "nowrap" }}>종합 요약</span>
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2, whiteSpace: "nowrap" }}>(3개 소스 통합)</div>
          </div>
          
          {/* 2. 기간 총 근무 가능한 시간 */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, whiteSpace: "nowrap" }}>총 근무 가능</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>{fmtMin(totalWorkingMinutes)}</div>
          </div>
          
          {/* 3. 공휴일(차감) */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 4, whiteSpace: "nowrap" }}>공휴일 (차감)</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8" }}>{fmtMin(holidayMinutes)}</div>
          </div>
          
          {/* 4. 대체휴무(차감) */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#fb7185", marginBottom: 4, whiteSpace: "nowrap" }}>대휴 (차감)</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#e11d48" }}>{fmtMin(substituteLeaveMinutes)}</div>
          </div>
          
          {/* 5. 연차/반차(차감) */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#f43f5e", marginBottom: 4, whiteSpace: "nowrap" }}>연차/반차 (차감)</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#be123c" }}>{fmtMin(annualLeaveMinutes)}</div>
          </div>

          {/* 6. 실제 근무 가능한 시간 */}
          <div style={{ textAlign: "center", borderLeft: "1px solid #334155", borderRight: "1px solid #334155", padding: "0 2px" }}>
            <div style={{ fontSize: 11, color: "#38bdf8", marginBottom: 4, fontWeight: 600, whiteSpace: "nowrap" }}>실 근무 가능</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#38bdf8" }}>{fmtMin(actualAvailableMinutes)}</div>
          </div>
          
          {/* 7. 실제 총 업무건수 */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#60a5fa", marginBottom: 4, whiteSpace: "nowrap" }}>실제 업무건수</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#2563eb" }}>{total.count}<span style={{ fontSize: 11, fontWeight: 400, color: "#fff" }}>건</span></div>
          </div>

          {/* 8. 실제 총 근무한 시간 */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#34d399", marginBottom: 4, fontWeight: 600, whiteSpace: "nowrap" }}>실제 근무시간</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#10b981" }}>{fmtMin(total.total_minutes)}</div>
          </div>
          
          {/* 9. 평균 난이도 (미지정 제외 가중평균) */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, whiteSpace: "nowrap" }}>평균 난이도</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{total.avg_difficulty}<span style={{ fontSize: 11, color: "#94a3b8" }}>/10</span></div>
          </div>
        </div>
      )}

      {err && (
        <div style={{ ...card, color: "#b91c1c", marginBottom: 16 }}>{err}</div>
      )}
      {loading && (
        <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 14 }}>불러오는 중…</div>
      )}

      {!loading && data && (
        <>
          {/* ── 소스별 카드 3개 그리드 ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 16, alignItems: "start" }}>
            {["calendar", "task", "daily"].map(key => {
              const meta = SOURCE_META[key];
              const s = data[key] || { count: 0, total_minutes: 0, avg_difficulty: 0, by_difficulty: [] };
              
              return (
                <div key={key} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ ...card, display: "flex", flexDirection: "column" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14 }}>
                        <span style={{ fontSize: 16 }}>{meta.icon}</span>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{meta.label}</span>
                        <span style={{
                          marginLeft: "auto", width: 8, height: 8, borderRadius: "50%", background: meta.color,
                        }} />
                      </div>
                      
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 16 }}>
                        <div>
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>건수</div>
                          <div style={{ fontSize: 22, fontWeight: 600, color: meta.color }}>
                            {Array.isArray(s.items) ? s.items.filter(it => !LEAVE_CATEGORIES.includes(it.category || it.category_name)).length : 0}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>총 시간</div>
                          <div style={{ fontSize: 18, fontWeight: 600 }}>
                            {fmtMin(Array.isArray(s.items) ? s.items.filter(it => !LEAVE_CATEGORIES.includes(it.category || it.category_name)).reduce((a, b) => a + (b.minutes || 0), 0) : 0)}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>평균 난이도</div>
                          <div style={{ fontSize: 18, fontWeight: 600 }}>
                            {(() => {
                              const validItems = (s.items || []).filter(it => !LEAVE_CATEGORIES.includes(it.category || it.category_name) && (Number(it.difficulty) || 0) > 0);
                              if(!validItems.length) return 0;
                              const sum = validItems.reduce((a, b) => a + (Number(b.difficulty) || 0), 0);
                              return Math.round((sum / validItems.length) * 10) / 10;
                            })()} <span style={{ fontSize: 12, color: "#94a3b8" }}>/10</span>
                          </div>

                        </div>
                      </div>

                    </div>


                    <div style={{ marginTop: "auto", borderTop: "1px dashed #e2e8f0", paddingTop: 10 }}>



                      <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>
                        난이도별 상세 분포 (휴가 제외)
                      </div>
                      {Array.from({ length: 10 }, (_, i) => {
                        const level = i + 1;
                        const validItems = (s.items || []).filter(it => !LEAVE_CATEGORIES.includes(it.category || it.category_name) && Number(it.difficulty) === level);
                        const count = validItems.length;
                        const mins = validItems.reduce((a, b) => a + (Number(b.minutes) || 0), 0);

                        return (
                          <div key={level} style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            padding: "3px 0", fontSize: 12, borderBottom: "0.5px solid #f1f5f9",
                            color: count > 0 ? "#0f172a" : "#94a3b8"
                          }}>
                            <span style={{ fontWeight: count > 0 ? 600 : 400 }}>난이도 {level}</span>
                            <span style={{ fontSize: 11 }}>
                              {count > 0 ? `${count}건 / ${fmtMin(mins)}` : "—"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* 캘린더 업무 통계 테이블 */}
                  {key === "calendar" && (
                    <div style={{ ...card }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                        <span>📋</span> 캘린더 업무 항목별 상세 통계
                      </div>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead>
                            <tr style={{ color: "#64748b", textAlign: "right", borderBottom: "0.5px solid #e2e8f0" }}>
                              <th style={{ textAlign: "left", padding: "8px 4px" }}>업무 항목명</th>
                              <th style={{ padding: "8px 4px", width: "70px" }}>건수</th>
                              <th style={{ padding: "8px 4px", width: "95px" }}>소요 시간</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.calendar_categories && data.calendar_categories.length > 0 ? (

                              data.calendar_categories.filter(c => !LEAVE_CATEGORIES.includes(c.category_name)).map((item, idx) => (
                                <tr key={idx}
                                  onClick={() => openDetail(item.category_name, item.items, "#3b82f6")}
                                  onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                                  style={{ borderBottom: "0.5px solid #f1f5f9", textAlign: "right", cursor: "pointer" }}>
                                  <td style={{ textAlign: "left", padding: "10px 4px", fontWeight: 500, color: "#334155" }}>
                                    {item.category_name || "미지정"}
                                  </td>
                                  <td style={{ padding: "10px 4px", color: "#3b82f6", fontWeight: 600, textDecoration: "underline" }}>{item.count}건</td>
                                  <td style={{ padding: "10px 4px", fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap" }}>{fmtMin(item.total_minutes)}</td>
                                </tr>
                              ))


                            ) : (
                              <tr><td colSpan="3" style={{ textAlign: "center", padding: "24px 0", color: "#94a3b8" }}>조회된 업무 항목이 없습니다.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* 지시 업무 통계 테이블 */}
                  {key === "task" && (
                    <div style={{ ...card }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                        <span>📋</span> 지시 업무 항목별 상세 통계
                      </div>

                      {/* 🆕 업무 지시 구분 3분할 */}
                      {data.task_breakdown && (
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)",
                          gap: 8,
                          marginBottom: 14,
                          paddingBottom: 14,
                          borderBottom: "1px dashed #e2e8f0",
                        }}>
                          {[
                            { k: "assigned_by_me",           label: "내가 지시",   color: "#0ea5e9", icon: "📤" },
                            { k: "assigned_to_me",           label: "내가 받음",   color: "#8b5cf6", icon: "📥" },
                            { k: "assigned_to_me_completed", label: "받아서 완료", color: "#10b981", icon: "✅" },
                          ].map(item => {
                            const stat = data.task_breakdown[item.k] || { count: 0, total_minutes: 0 };
                            return (
                              <div key={item.k} style={{
                                background: "#f8fafc",
                                border: "0.5px solid #e2e8f0",
                                borderRadius: 8,
                                padding: "10px 8px",
                                textAlign: "center",
                              }}>
                                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6, whiteSpace: "nowrap" }}>
                                  {item.icon} {item.label}
                                </div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: item.color, lineHeight: 1.2 }}>
                                  {stat.count}
                                  <span style={{ fontSize: 11, fontWeight: 400, color: "#64748b" }}>건</span>
                                </div>
                                <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                                  {fmtMin(stat.total_minutes)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        
                          <thead>
                            <tr style={{ color: "#64748b", textAlign: "right", borderBottom: "0.5px solid #e2e8f0" }}>
                              <th style={{ textAlign: "left", padding: "8px 4px" }}>업무 항목명</th>
                              <th style={{ padding: "8px 4px", width: "70px" }}>건수</th>
                              <th style={{ padding: "8px 4px", width: "95px" }}>소요 시간</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.task_categories && data.task_categories.length > 0 ? (
                              data.task_categories.filter(c => !LEAVE_CATEGORIES.includes(c.category_name)).map((item, idx) => (
                                <tr key={idx}
                                  onClick={() => openDetail(item.category_name, item.items, "#8b5cf6")}
                                  onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                                  style={{ borderBottom: "0.5px solid #f1f5f9", textAlign: "right", cursor: "pointer" }}>
                                  <td style={{ textAlign: "left", padding: "10px 4px", fontWeight: 500, color: "#334155" }}>
                                    {item.category_name || "미지정"}
                                  </td>
                                  <td style={{ padding: "10px 4px", color: "#8b5cf6", fontWeight: 600, textDecoration: "underline" }}>{item.count}건</td>
                                  <td style={{ padding: "10px 4px", fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap" }}>{fmtMin(item.total_minutes)}</td>
                                </tr>
                              ))

                            ) : (
                              <tr><td colSpan="3" style={{ textAlign: "center", padding: "24px 0", color: "#94a3b8" }}>조회된 업무 항목이 없습니다.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* 일상 업무 통계 테이블 */}
                  {key === "daily" && (
                    <div style={{ ...card }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                        <span>⚡</span> 일상 업무 항목별 상세 통계
                      </div>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead>
                            <tr style={{ color: "#64748b", textAlign: "right", borderBottom: "0.5px solid #e2e8f0" }}>
                              <th style={{ textAlign: "left", padding: "8px 4px" }}>업무 항목명</th>
                              <th style={{ padding: "8px 4px", width: "70px" }}>건수</th>
                              <th style={{ padding: "8px 4px", width: "95px" }}>소요 시간</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.daily_categories && data.daily_categories.length > 0 ? (

                              data.daily_categories.filter(c => !LEAVE_CATEGORIES.includes(c.category_name)).map((item, idx) => (
                                <tr key={idx}
                                  onClick={() => openDetail(item.category_name, item.items, "#10b981")}
                                  onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                                  style={{ borderBottom: "0.5px solid #f1f5f9", textAlign: "right", cursor: "pointer" }}>
                                  <td style={{ textAlign: "left", padding: "10px 4px", fontWeight: 500, color: "#334155" }}>
                                    {item.category_name || "미지정"}
                                  </td>
                                  <td style={{ padding: "10px 4px", color: "#10b981", fontWeight: 600, textDecoration: "underline" }}>{item.count}건</td>
                                  <td style={{ padding: "10px 4px", fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap" }}>{fmtMin(item.total_minutes)}</td>
                                </tr>
                              ))


                            ) : (
                              <tr><td colSpan="3" style={{ textAlign: "center", padding: "24px 0", color: "#94a3b8" }}>조회된 업무 항목이 없습니다.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 팀 모드: 직원별 성과 */}
          {data.scope === "team" && Array.isArray(data.by_employee) && (
            <div style={{ ...card, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>직원별 성과</div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ color: "#64748b", textAlign: "right", borderBottom: "0.5px solid #e2e8f0" }}>
                      <th style={{ textAlign: "left", padding: "8px 6px" }}>직원</th>
                      <th style={{ padding: "8px 6px" }}>📅 건/분</th>
                      <th style={{ padding: "8px 6px" }}>📋 건/분</th>
                      <th style={{ padding: "8px 6px" }}>⚡ 건/분</th>
                      <th style={{ padding: "8px 6px" }}>총 건수</th>
                      <th style={{ padding: "8px 6px" }}>총 시간</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_employee.map(emp => {
                      const t = combineTotal(emp);
                      return (
                        <tr key={emp.employee_id} style={{ borderBottom: "0.5px solid #f1f5f9", textAlign: "right" }}>
                          <td style={{ textAlign: "left", padding: "8px 6px", fontWeight: 500 }}>{emp.employee_name}</td>
                          <td style={{ padding: "8px 6px" }}>{emp.calendar.count} / {emp.calendar.total_minutes}</td>
                          <td style={{ padding: "8px 6px" }}>{emp.task.count} / {emp.task.total_minutes}</td>
                          <td style={{ padding: "8px 6px" }}>{emp.daily.count} / {emp.daily.total_minutes}</td>
                          <td style={{ padding: "8px 6px", fontWeight: 600 }}>{t.count}</td>
                          <td style={{ padding: "8px 6px" }}>{fmtMin(t.total_minutes)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>

    {/* ── 우측: 핵심 업무 패널 ── */}
    {!loading && data && (
      <div style={{ flex: "0 0 300px", width: 300, marginTop: 16 }}>
        <div style={{ ...card, padding: "16px 16px" }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
            <span>🎯</span> 핵심 업무
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 14 }}>
            난이도 7 이상 업무
          </div>

          {keyTasks.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {keyTasks.map((t, idx) => {
                const meta = SOURCE_META[t.source];
                return (
                  <div key={idx} style={{
                    border: "0.5px solid #e2e8f0", borderRadius: 8,
                    padding: "10px 12px", borderLeft: `3px solid ${meta.color}`,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
                      <span style={{ fontSize: 12 }}>{meta.icon}</span>
                      <span style={{ fontSize: 10, color: "#94a3b8" }}>{t.date}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginBottom: 6, lineHeight: 1.3 }}>
                      {t.title}
                    </div>
                    <div style={{ display: "flex", gap: 8, fontSize: 11 }}>
                      <span style={{
                        background: t.difficulty >= 7 ? "#fef2f2" : "#f1f5f9",
                        color: t.difficulty >= 7 ? "#dc2626" : "#64748b",
                        borderRadius: 4, padding: "2px 7px", fontWeight: 600,
                      }}>
                        난이도 {t.difficulty || "—"}
                      </span>
                      <span style={{
                        background: t.minutes >= 120 ? "#eff6ff" : "#f1f5f9",
                        color: t.minutes >= 120 ? "#2563eb" : "#64748b",
                        borderRadius: 4, padding: "2px 7px", fontWeight: 600,
                      }}>
                        {fmtMin(t.minutes)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8", fontSize: 13 }}>
              핵심 업무가 없습니다.
            </div>
          )}
        </div>
      </div>

    )}

    {detail && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        {...detailBackdrop}>
        <div style={{ background: "#fff", borderRadius: 12, padding: 20, width: 560,
          maxWidth: "90vw", maxHeight: "80vh", overflowY: "auto",
          fontFamily: "'Noto Sans KR', sans-serif" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: detail.color }} />
            <span style={{ fontSize: 16, fontWeight: 700 }}>{detail.title}</span>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              · {detail.items.reduce((a, it) => a + (Number(it.count) || 1), 0)}건
            </span>
            <button onClick={() => setDetail(null)}
              style={{ marginLeft: "auto", border: "none", background: "transparent",
                fontSize: 18, cursor: "pointer", color: "#94a3b8" }}>✕</button>
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 12 }}>합산된 개별 건 목록</div>

          {detail.items.length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8", fontSize: 13 }}>
              상세 내역이 없습니다.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ color: "#64748b", borderBottom: "0.5px solid #e2e8f0", textAlign: "left" }}>
                  <th style={{ padding: "8px 6px", width: 92 }}>날짜</th>
                  <th style={{ padding: "8px 6px" }}>제목</th>
                  <th style={{ padding: "8px 6px", width: 70, textAlign: "center" }}>담당자</th>
                  <th style={{ padding: "8px 6px", width: 56, textAlign: "center" }}>난이도</th>
                  <th style={{ padding: "8px 6px", width: 80, textAlign: "right" }}>시간</th>
                </tr>
              </thead>
              <tbody>
                {[...detail.items]
                  .sort((a, b) => String(a.date).localeCompare(String(b.date)))
                  .map((it, i) => (
                    <tr key={i} style={{ borderBottom: "0.5px solid #f1f5f9" }}>
                      <td style={{ padding: "8px 6px", color: "#64748b", whiteSpace: "nowrap" }}>{it.date || "—"}</td>
                      <td style={{ padding: "8px 6px", color: "#0f172a" }}>
                        {it.title || "(제목 없음)"}
                        {it.note ? <span style={{ color: "#94a3b8" }}> · {it.note}</span> : null}
                        {it.count && it.count > 1 ? <span style={{ color: "#94a3b8" }}> ×{it.count}</span> : null}
                      </td>
                      <td style={{ padding: "8px 6px", textAlign: "center", color: "#475569", whiteSpace: "nowrap" }}>{it.employee_name || "—"}</td>
                      <td style={{ padding: "8px 6px", textAlign: "center", color: "#475569" }}>{it.difficulty || "—"}</td>
                      <td style={{ padding: "8px 6px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>{fmtMin(it.minutes)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    )}

    {showPerm && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        {...permBackdrop}>


        <div style={{ background: "#fff", borderRadius: 12, padding: 20, width: 280,
          maxHeight: "70vh", overflowY: "auto" }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>업무성과 열람 권한</div>
          {allUsers.map(u => (
            <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 10,
              padding: "8px 4px", fontSize: 14, cursor: "pointer",
              borderBottom: "0.5px solid #f1f5f9" }}>
              <input type="checkbox" checked={draftIds.includes(u.id)}
                onChange={() => toggleDraft(u.id)}
                style={{ width: 16, height: 16, flex: "0 0 auto" }} />
              <span style={{ whiteSpace: "nowrap" }}>{u.name}</span>
            </label>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
            <button style={ghostBtn} onClick={() => setShowPerm(false)}>취소</button>
            <button style={{ ...ghostBtn, background: "#2563eb", color: "#fff", border: "none" }}
              onClick={savePerm}>저장</button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}

